import { useEffect, useState } from 'react';
import { getCampaingInformation_api } from 'api/adminApi';
import useScreen from 'Hooks/useScreen';
import ButtonSymphony from 'elements/ButtonSymphony';
import ModalSymphony from 'elements/ModalSymphony';
import Loader from 'elements/Loader';
import Sty from 'modules/Sty';

interface CampaignInfo {
    trackingError?: string;
    errors?: { code: string, name: string }[];
    campaignIds?: string[];
    adAccount?: string;
    accessToken?: string;
    fbPage: { id: string, name: string };
}

interface AdminShowCampaignInfoProps {
    isOpen: boolean;
    onClose: () => void;
    campaignId: number | string | undefined;
}

const AdminShowCampaignInfo = ({
    isOpen,
    onClose,
    campaignId
}: AdminShowCampaignInfoProps) => {
    const { mobileView } = useScreen();

    const [campaignInfo, setCampaignInfo] = useState<CampaignInfo | null>(null);
    const [isLoadingCampaignInfo, setLoadingCampaignInfo] = useState<boolean>(false);

    const getCampaignInfo = async () => {
        setLoadingCampaignInfo(true);
        try {
            const res = await getCampaingInformation_api(campaignId as number);
            const { campaignInfo: { campaign_metadata } } = res;
            const { trackingError, errors, platform_details, campaign_state } = campaign_metadata;
            setCampaignInfo({
                trackingError: trackingError?.message,
                errors,
                campaignIds: platform_details?.campaigns,
                adAccount: platform_details?.ad_account,
                accessToken: platform_details?.access_token,
                fbPage: { id: campaign_state?.accounts?.fbPage.id, name: campaign_state?.accounts?.fbPage.name }
            });
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingCampaignInfo(false);
        }
    }

    useEffect(() => {
        getCampaignInfo();
    }, []);

    return (
        <ModalSymphony
            className="adminShowCampaignInfoModal"
            isOpen={isOpen}
            onClose={onClose}
            title="Admin: Show Campaign Info"
            subtitle="Show general data to troubleshoot campaign issues."
        >
            {isLoadingCampaignInfo && <Loader />}
            {campaignInfo &&
                <div className="adminShowCampaignInfoModalContent">
                    <div><span style={Sty.text16bold}>Tracking Error:</span> {campaignInfo.trackingError ?? 'No tracking error information available'}</div>
                    <div><span style={Sty.text16bold}>Errors: </span>{campaignInfo.errors?.map(error => error.code).join(', ') || 'No errors reported'}</div>
                    <div><span style={Sty.text16bold}>Campaign IDs: </span>{campaignInfo.campaignIds?.join(", ") || "There are no campaign ids"}</div>
                    <div><span style={Sty.text16bold}>Ad Account: </span>{campaignInfo.adAccount ?? 'No ad account information available'}</div>
                    <div><span style={Sty.text16bold}>Facebook Page Name: </span>{campaignInfo.fbPage.name}</div>
                    <div><span style={Sty.text16bold}>Facebook Page ID: </span>{campaignInfo.fbPage.id}</div>
                    <div style={{ wordWrap: 'break-word' }}><span style={Sty.text16bold}>Access Token: </span>{campaignInfo.accessToken ?? 'No access token available'}</div>
                </div>
            }
        </ModalSymphony>
    );
};

export default AdminShowCampaignInfo;